// Admin-only API surfaces.
//
// These endpoints are gated by RequireRole(admin) on the backend. The panel
// guards them at the router layer too so a merchant user clicking around
// dev-tools can't reach them — defense in depth, the backend is still the
// authoritative gate.
//
// The single endpoint here today is the payout drill-down: it returns
// raw provider response data via the audit_log JSON columns, which is
// the reason it's admin-only (merchants get the status / error_code /
// error_message slice on the list endpoint and that's their cap).
import { apiClient } from "./client";
import type { CancelBlockedReason } from "./payouts";

// Mirrors the backend payoutItem shape but with amount_minor normalised
// to bigint at the deserialization boundary — see api/payouts.ts for the
// json-bigint rationale.
interface RawAdminPayout {
  id: number;
  merchant_id: number;
  batch_id: number | null;
  request_id: string;
  status: "needs_approval" | "pending" | "success" | "failure";
  amount_minor: number | bigint;
  currency: string;
  txn_mode: number;
  provider_txn_id: string | null;
  utr_no: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  can_cancel?: boolean;
  cancel_blocked_reason?: CancelBlockedReason | "";
  export_id?: number | null;
}

export interface AdminPayoutMerchant {
  id: number;
  slug: string;
  display_name: string;
  status: string;
}

export interface AdminPayoutBankAccount {
  id: number;
  label: string;
  provider: string;
  currency: string;
  status: string;
}

export interface AdminPayoutBeneficiary {
  id: number;
  name: string;
  account_no: string;
  ifsc: string;
  provider_beneid: string | null;
  is_registered: boolean;
}

/**
 * AdminAuditEntry — one row from audit_log scoped to this payout.
 *
 * `before` and `after` are arbitrary JSON written by the backend on every
 * state transition (action="payout.initiate" carries the provider
 * Initiate request/response; "payout.refresh" the CheckStatus response;
 * "payout.webhook" the inbound callback body; etc.). We pass them through
 * as `unknown` because the shape varies per action — the rendering layer
 * pretty-prints them rather than mapping a typed schema.
 */
export interface AdminAuditEntry {
  id: number;
  actor_user_id: number | null;
  actor_role: string | null;
  action: string;
  before: unknown;
  after: unknown;
  request_id: string | null;
  ip: string | null;
  created_at: string;
}

export interface AdminPayoutDetail {
  payout: {
    id: number;
    merchant_id: number;
    batch_id: number | null;
    request_id: string;
    status: "needs_approval" | "pending" | "success" | "failure";
    amount_minor: bigint;
    currency: string;
    txn_mode: number;
    provider_txn_id: string | null;
    utr_no: string | null;
    error_code: string | null;
    error_message: string | null;
    created_at: string;
    updated_at: string;
    /** Whether POST :id/cancel would succeed — decided by the backend, never
     *  re-derived here. See the Payout type in src/api/payouts.ts for why. */
    can_cancel?: boolean;
    cancel_blocked_reason?: CancelBlockedReason | "";
    /** The export holding this payout, when cancel_blocked_reason is
     *  already_exported. */
    export_id?: number | null;
  };
  merchant: AdminPayoutMerchant | null;
  bank_account: AdminPayoutBankAccount | null;
  beneficiary: AdminPayoutBeneficiary | null;
  audit_trail: AdminAuditEntry[];
}

interface RawAdminPayoutDetail {
  payout: RawAdminPayout;
  merchant: AdminPayoutMerchant | null;
  bank_account: AdminPayoutBankAccount | null;
  beneficiary: AdminPayoutBeneficiary | null;
  audit_trail: AdminAuditEntry[];
}

/**
 * getAdminPayoutDetail — GET /v1/admin/payouts/:id
 *
 * Admin-only. Returns the full drill-down: payout + merchant + bank rail +
 * beneficiary + every audit_log entry for this payout (newest first).
 *
 * 404 from the backend → axios throws (the caller can map via
 * apiErrorMessage); we don't transform that here.
 */
export async function getAdminPayoutDetail(
  payoutId: number,
): Promise<AdminPayoutDetail> {
  const resp = await apiClient.get<RawAdminPayoutDetail>(
    `/v1/admin/payouts/${payoutId}`,
  );
  return {
    ...resp.data,
    payout: {
      ...resp.data.payout,
      amount_minor: BigInt(resp.data.payout.amount_minor),
    },
  };
}

// ─── Provider calls (PSP outbound log) ───────────────────────────────────────

export interface ProviderCall {
  id: number;
  created_at: string;
  provider: string;
  endpoint: string;
  bank_account_id: number | null;
  payout_id: number | null;
  beneficiary_id: number | null;
  http_status: number;
  transport_error: string | null;
  duration_ms: number;
  provider_code: string | null;
  provider_status: string | null;
  provider_message: string | null;
  success: boolean;
  request_body: string;
  response_body: string;
  request_id: string | null;
}

export interface ProviderCallsListParams {
  provider?: string;
  endpoint?: string;
  success?: boolean;
  before?: string; // ISO timestamp cursor
  limit?: number;
}

/**
 * listProviderCalls — GET /v1/admin/provider-calls
 *
 * Admin-only. Returns the outbound PSP call log, newest-first. Bodies
 * include account_no on AccountVerification calls — the route is admin-only
 * precisely because that's PII merchants must never see.
 */
export async function listProviderCalls(
  params: ProviderCallsListParams = {},
): Promise<{ items: ProviderCall[]; limit: number }> {
  const search = new URLSearchParams();
  if (params.provider) search.set("provider", params.provider);
  if (params.endpoint) search.set("endpoint", params.endpoint);
  if (params.success != null) search.set("success", String(params.success));
  if (params.before) search.set("before", params.before);
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  const resp = await apiClient.get<{ items: ProviderCall[]; limit: number }>(
    `/v1/admin/provider-calls${qs ? `?${qs}` : ""}`,
  );
  return resp.data;
}

/**
 * getProviderCall — GET /v1/admin/provider-calls/:id
 *
 * Single-row drill-down — used by the panel's detail modal so each entry
 * can be deep-linked from a future support workflow.
 */
export async function getProviderCall(id: number): Promise<ProviderCall> {
  const resp = await apiClient.get<ProviderCall>(
    `/v1/admin/provider-calls/${id}`,
  );
  return resp.data;
}

// ────────────────────────────────────────────────────────────────────────
// PDF round 3 N5 — cross-tenant bank-rails view.
// ────────────────────────────────────────────────────────────────────────
//
// GET /v1/admin/bank-rails returns every rail across every merchant with the
// IDFC credential triple obfuscated as `f****l`. Built for "are two
// merchants accidentally sharing IDFC credentials?" debugging — the
// obfuscation is stable enough that a shared triple shows up byte-identical
// across rows.
//
// Buckzy rails surface with credential=null (Buckzy credentials are
// per-merchant, not per-rail).

export interface AdminRailCredential {
  id: number;
  base_url: string;
  environment: string;
  email_obfuscated: string;
  merchant_key_obfuscated: string;
  password_obfuscated: string;
}

export interface AdminRail {
  id: number;
  merchant_id: number;
  merchant_slug: string;
  merchant_display_name: string;
  label: string;
  provider: string;
  status: string;
  created_at: string;
  disabled_at: string | null;
  /** The operator's off switch (backend migration 050). `status` already
   *  reflects it as "disabled"; this is the raw flag, and disabled_reason
   *  is the note they left. */
  disabled?: boolean;
  disabled_reason?: string | null;
  credential: AdminRailCredential | null;
}

/**
 * listAdminBankRails — GET /v1/admin/bank-rails
 *
 * Admin-only. Returns every bank rail across every merchant. Used by the
 * /admin/rails page to spot credential sharing across tenants.
 */
export async function listAdminBankRails(): Promise<{ rails: AdminRail[] }> {
  const resp = await apiClient.get<{ rails: AdminRail[] }>(
    `/v1/admin/bank-rails`,
  );
  return resp.data;
}

// ────────────────────────────────────────────────────────────────────────
// Admin-only cross-tenant audit log viewer.
// ────────────────────────────────────────────────────────────────────────
//
// Backed by GET /v1/admin/audit-log. Audit-log rows carry before/after as
// raw jsonb — the backend renders them as JSON null literals when the
// column is NULL/empty so JSON.parse is uniform on the panel side.

export interface AdminAuditLogEntry {
  id: number;
  actor_user_id: number | null;
  // Resolved by the backend via auth-service's user directory. Empty
  // string when actor_user_id is null, when the caller lacks
  // payouts:users:read, or when the user no longer exists in auth-service.
  actor_email?: string;
  actor_role: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  // jsonb pass-through — string-stringified by axios? No: json-bigint
  // parses it into a real JS value. We keep `unknown` so the consumer is
  // forced to narrow before rendering.
  before: unknown;
  after: unknown;
  request_id: string | null;
  ip: string | null;
  created_at: string;
}

export interface AuditLogListParams {
  resource_type?: string;
  resource_id?: string;
  action?: string;
  actor_user_id?: number;
  since?: string;
  before?: string;
  limit?: number;
}

/**
 * listAdminAuditLog — GET /v1/admin/audit-log
 *
 * Admin-only. Returns audit-log rows newest-first across every tenant.
 * before/after come back as raw jsonb (null literal when DB column was
 * null), wrapped here as `unknown` so each render site must narrow.
 */
export async function listAdminAuditLog(
  params: AuditLogListParams = {},
): Promise<{ items: AdminAuditLogEntry[]; limit: number }> {
  const search = new URLSearchParams();
  if (params.resource_type) search.set("resource_type", params.resource_type);
  if (params.resource_id) search.set("resource_id", params.resource_id);
  if (params.action) search.set("action", params.action);
  if (params.actor_user_id != null)
    search.set("actor_user_id", String(params.actor_user_id));
  if (params.since) search.set("since", params.since);
  if (params.before) search.set("before", params.before);
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  const resp = await apiClient.get<{
    items: AdminAuditLogEntry[];
    limit: number;
  }>(`/v1/admin/audit-log${qs ? `?${qs}` : ""}`);
  return resp.data;
}
